import type { Model, ReasoningEffort } from './types';

export interface ChildConversationOverride {
    model?: Model;
    reasoningEffort?: ReasoningEffort;
}

/** A tool's class, used as the identity an override is stored against. */
type ToolClass = Function;

/**
 * Session-scoped child-conversation overrides, keyed by tool class.
 *
 * Keyed by the class rather than held on the tool instance because tool instances are
 * disposable: an agent rebuilds its whole tool set whenever its async context loads or
 * its luminary changes, and `getAllTools()` builds a fresh set on every call. An
 * override stored on the instance the Tools modal happens to hold is therefore written
 * to an object that never executes anything. The class is a module singleton, so it
 * survives those rebuilds.
 *
 * Not keyed by tool name: two classes can declare the same name at different child
 * settings (`image_ask` is both ImageAskTool and LegacyImageAskTool), and a name key
 * would let a pick on one surface silently retune the other's subagent.
 *
 * Nothing persists this, so a reload returns every tool to the values it declares
 * in code.
 */
const overridesByToolClass = new Map<ToolClass, ChildConversationOverride>();

export function getChildConversationOverride(
    toolClass: ToolClass,
): ChildConversationOverride | undefined {
    return overridesByToolClass.get(toolClass);
}

function update(toolClass: ToolClass, patch: ChildConversationOverride): void {
    const next = { ...overridesByToolClass.get(toolClass), ...patch };
    if (next.model === undefined && next.reasoningEffort === undefined) {
        overridesByToolClass.delete(toolClass);
        return;
    }
    overridesByToolClass.set(toolClass, next);
}

export function setChildModelOverride(toolClass: ToolClass, model: Model | undefined): void {
    update(toolClass, { model });
}

export function setChildReasoningEffortOverride(
    toolClass: ToolClass,
    reasoningEffort: ReasoningEffort | undefined,
): void {
    update(toolClass, { reasoningEffort });
}

/** Drops every override, returning all tools to their declared values. */
export function clearChildConversationOverrides(): void {
    overridesByToolClass.clear();
}
